import React, { useState } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { type InstructorDetails, type SurveyCompletionRate, DivisionCategory } from '../../../../models/Division'
import { BookOutlined, AccessTime, AccountTreeOutlined } from '@mui/icons-material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined'
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import { Link, useSearchParams } from 'react-router-dom'
import ROUTES from '../../../../consts/paths'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import { grey } from '../../../../colors'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'

import pluralize from 'pluralize'
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined'
import CustomTooltip from '../../../../components/CustomTooltip'
import { SurveyScoreTooltipContent } from '../SurveyScoreInfo'
export interface ReportsHeaderProps {
  completionRate: SurveyCompletionRate
  parentSurveyScore?: number | null
  percent?: number
  attributesNode?: React.ReactNode
}

const ACTIVE_INSTRUCTOR_LIMIT = 3
const RESTRICTED_INSTRUCTOR_VISIBLE = 1

const expandChipSx = {
  bgcolor: grey[200],
  color: grey[800],
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { bgcolor: grey[300] }
}

// CE-39 follow-up: isInScope is emitted only when the caller has a Faculty
// Attribute scope. Treat absent / true as accessible; only group separately
// when explicitly false.
const SectionInstructors: React.FC<{ instructors: InstructorDetails[] }> = ({ instructors }) => {
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('seriesId')
  const [activeExpanded, setActiveExpanded] = useState(false)

  const iconStyle = { sx: { color: 'iconColor' } }

  const active = instructors.filter(i => i?.isInScope !== false)
  const restricted = instructors.filter(i => i?.isInScope === false)

  if (active.length === 0 && restricted.length === 0) return null

  const activeOverLimit = active.length > ACTIVE_INSTRUCTOR_LIMIT
  const activeVisible = activeExpanded || !activeOverLimit ? active : active.slice(0, ACTIVE_INSTRUCTOR_LIMIT)
  const activeHidden = active.length - ACTIVE_INSTRUCTOR_LIMIT

  const restrictedOverLimit = restricted.length > RESTRICTED_INSTRUCTOR_VISIBLE
  const restrictedVisible = restricted.slice(0, RESTRICTED_INSTRUCTOR_VISIBLE)
  const restrictedHidden = restricted.slice(RESTRICTED_INSTRUCTOR_VISIBLE)

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
      {active.length > 0 && (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
          <AccountCircleOutlinedIcon {...iconStyle} />
          {activeVisible.map((instructor, index) => {
            const isLast = index === activeVisible.length - 1
            const showComma = !isLast || activeOverLimit
            return (
              <Box display="flex" alignItems="center" key={instructor?.id ?? index}>
                <Link
                  target="_blank"
                  style={{ color: grey[700], whiteSpace: 'nowrap' }}
                  to={`${ROUTES.INSTRUCTOR_SURVEYS}/${instructor?.id ?? ''}?termId=${termId}&seriesId=${seriesId}`}
                >
                  {instructor?.title}
                </Link>
                <ArrowOutwardIcon sx={{ fontSize: '20px' }} />
                {showComma && ','}
              </Box>
            )
          })}
          {activeOverLimit && (
            <Chip
              size="small"
              label={activeExpanded ? 'Show less' : `+${activeHidden} more`}
              onClick={() => { setActiveExpanded(prev => !prev) }}
              aria-expanded={activeExpanded}
              sx={expandChipSx}
            />
          )}
        </Box>
      )}
      {restricted.length > 0 && (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
          <CustomTooltip
            title="You don't have permission to view these instructors' data."
            placement="bottom"
          >
            <PersonOffOutlinedIcon {...iconStyle} aria-label="Restricted instructors" />
          </CustomTooltip>
          {restrictedVisible.map((instructor, index) => {
            const isLast = index === restrictedVisible.length - 1
            const showComma = !isLast || restrictedOverLimit
            return (
              <Box display="flex" alignItems="center" key={instructor?.id ?? `restricted-${index}`}>
                <Typography sx={{ color: grey[700], fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {instructor?.title}
                </Typography>
                {showComma && ','}
              </Box>
            )
          })}
          {restrictedOverLimit && (
            <CustomTooltip
              title={
                <Box>
                  {restrictedHidden.map((instructor, index) => (
                    <Typography
                      key={instructor?.id ?? `restricted-hidden-${index}`}
                      variant="body2"
                      sx={{ fontSize: 12, color: 'white' }}
                    >
                      {instructor?.title}
                    </Typography>
                  ))}
                </Box>
              }
              placement="bottom"
            >
              <Chip
                size="small"
                label={`+${restrictedHidden.length} more`}
                sx={expandChipSx}
              />
            </CustomTooltip>
          )}
        </Box>
      )}
    </Box>
  )
}

// TAs are shown name-only (CE-158) — no clickable identity, unlike instructors.
const SectionTeachingAssistants: React.FC<{ names: string[] }> = ({ names }) => {
  const [expanded, setExpanded] = useState(false)

  const iconStyle = { sx: { color: 'iconColor' } }

  const overLimit = names.length > ACTIVE_INSTRUCTOR_LIMIT
  const visible = expanded || !overLimit ? names : names.slice(0, ACTIVE_INSTRUCTOR_LIMIT)
  const hidden = names.length - ACTIVE_INSTRUCTOR_LIMIT

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
      <SupervisorAccountOutlinedIcon {...iconStyle} />
      {visible.map((name, index) => {
        const isLast = index === visible.length - 1
        const showComma = !isLast || overLimit
        return (
          <Box display="flex" alignItems="center" key={`${name}-${index}`}>
            <Typography component='span' variant='inherit' sx={{ color: grey[700], whiteSpace: 'nowrap' }}>
              {name}
            </Typography>
            {showComma && ','}
          </Box>
        )
      })}
      {overLimit && (
        <Chip
          size="small"
          label={expanded ? 'Show less' : `+${hidden} more`}
          onClick={() => { setExpanded(prev => !prev) }}
          aria-expanded={expanded}
          sx={expandChipSx}
        />
      )}
    </Box>
  )
}

const ReportAttributes: React.FC<ReportsHeaderProps> = ({
  completionRate,
  parentSurveyScore,
  percent,
  attributesNode

}): JSX.Element => {
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('seriesId')
  const iconStyle = {
    sx: {
      color: 'iconColor'
    }
  }

  const formattedPercent = Math.round(percent ?? 0)
  const progressAriaLabel =
    completionRate.isAnonymousSurvey
      ? `${completionRate.studentCount} Responses`
      : ` ${completionRate.completionCount}/${completionRate.studentCount} (${formattedPercent}%) Responses`
  const parentScore = parentSurveyScore
  const formattedParentScore = parentScore?.toFixed(parentScore === 0 ? 1 : 2)
  // A null/undefined score (no_access nulls it server-side; a brand-new survey with zero
  // completions has no average yet either) renders as "--". An actual zero average keeps
  // hiding the badge entirely, preserving prior behavior.
  const scoreUnavailable = parentScore == null
  const showScoreBadge = scoreUnavailable || (formattedParentScore != null && formattedParentScore !== '0.0')
  const scoreBadge = showScoreBadge && (
    <CustomTooltip title={<SurveyScoreTooltipContent />} placement="bottom">
      <LeaderboardOutlinedIcon {...iconStyle} />
      {scoreUnavailable ? '--' : formattedParentScore}
    </CustomTooltip>
  )
  function renderSubtitle (): JSX.Element {
    switch (completionRate.categoryName) {
      case DivisionCategory.Course:
        return <Box display="flex" alignItems="center" height="100%" flexWrap="wrap" gap={3}>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            <BookOutlined {...iconStyle} />{completionRate.abbreviation}
          </Box>
          <Box display="flex" alignItems="center" height="100%" gap={0.75}>
            <VerifiedOutlinedIcon {...iconStyle} />{progressAriaLabel.trim()}
          </Box>
          {scoreBadge}
        </Box>
      case DivisionCategory.Section: {
        const allInstructors = completionRate?.instructorDetails ?? []
        const activeCount = allInstructors.filter(i => i?.isInScope !== false).length
        const restrictedCount = allInstructors.length - activeCount
        const inlineInstructors = activeCount > 0 && activeCount <= 3 && restrictedCount === 0
        const instructorsNode = <SectionInstructors instructors={allInstructors} />
        const teachingAssistantNames = (completionRate?.teachingAssistants ?? '')
          .split(',')
          .map(name => name.trim())
          .filter(name => name.length > 0)
        const teachingAssistantsNode = teachingAssistantNames.length > 0
          ? <SectionTeachingAssistants names={teachingAssistantNames} />
          : null
        return <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
            <Box display="flex" alignItems="center" flexWrap="nowrap" gap={0.75}>
              <AccessTime {...iconStyle} />{completionRate.abbreviation}
            </Box>
            {completionRate?.crosslistedAbbrevs &&
              <Box display="flex" alignItems="center" flexWrap="nowrap" gap={0.75}>
                <ShuffleIcon {...iconStyle} />{completionRate?.crosslistedAbbrevs}
              </Box>}
            <Box display="flex" alignItems="center" flexWrap="nowrap" gap={0.75}>
              <VerifiedOutlinedIcon {...iconStyle} />{progressAriaLabel}
            </Box>
            <Box display="flex" alignItems="center" flexWrap="nowrap" gap={0.75}>
              {scoreBadge}
            </Box>
            {attributesNode}
            {inlineInstructors && instructorsNode}
            {inlineInstructors && teachingAssistantsNode}
          </Box>
          {!inlineInstructors && (allInstructors.length > 0 || teachingAssistantsNode != null) && (
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
              {instructorsNode}
              {teachingAssistantsNode}
            </Box>
          )}
        </Box>
      }
      default:
        return <Box display="flex" alignItems="center" height="100%" gap={3}>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}><VerifiedOutlinedIcon {...iconStyle} />{progressAriaLabel.trim()}</Box>
          <Box display="flex" alignItems="center" height="100%" flexWrap="nowrap" gap={0.75}>
            {scoreBadge}
          </Box>
        </Box>
    }
  }
  return (
    <Typography tabIndex={0} variant="h3">
      {renderSubtitle()}
    </Typography>
  )
}

export default ReportAttributes
